import React, { useState, useEffect } from "react";
import { Input } from "./input";
import { cn } from "@/lib/utils";

type AutocompleteItem = {
  value: string;
  label: string;
};

interface AutocompleteInputProps {
  value: string;
  onChange: (value: string) => void;
  onSelect?: (value: string) => void;
  onBlur?: () => void;
  items: AutocompleteItem[];
  placeholder?: string;
  className?: string;
  autoFocus?: boolean;
  showAbove?: boolean;
}

export function AutocompleteInput({
  value,
  onChange,
  onSelect,
  onBlur,
  items,
  placeholder = "Search...",
  className,
  autoFocus,
  showAbove = true,
}: AutocompleteInputProps) {
  const [inputFocused, setInputFocused] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Filter items based on input (substring search)
  const filteredItems = items.filter((item) =>
    item.label.toLowerCase().includes(value.toLowerCase())
  );

  // Update suggestions visibility when input or focus changes
  useEffect(() => {
    setShowSuggestions(inputFocused && value.length > 0 && filteredItems.length > 0);
  }, [inputFocused, value, filteredItems.length]);

  return (
    <div className={cn("relative w-full", className)}>
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        onFocus={() => setInputFocused(true)}
        onBlur={() => {
          // Delay hiding suggestions to allow clicking on them
          setTimeout(() => {
            setInputFocused(false);
            onBlur?.();
          }, 200);
        }}
        autoFocus={autoFocus}
      />
      {showSuggestions && (
        <div
          className={cn(
            "absolute z-50 w-full bg-white border rounded-md shadow-lg max-h-60 overflow-auto",
            showAbove ? "bottom-full mb-1" : "top-full mt-1"
          )}
        >
          {filteredItems.map((item) => (
            <div
              key={item.value}
              className="px-3 py-2 cursor-pointer hover:bg-gray-100"
              onMouseDown={(e) => {
                // Prevent blur from closing dropdown
                e.preventDefault();
              }}
              onClick={() => {
                onChange(item.value);
                onSelect?.(item.value);
                setShowSuggestions(false);
              }}
            >
              {item.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}